<template>
  <div>
    <h1 class="text-h4 mb-2">Сайт (новости/статьи)</h1>
    <v-chip-group
      v-model="selectedSite"
      mandatory
      active-class="primary accent-4 white--text"
      class="mb-6"
    >
      <v-chip color="white" value="upmind">up-mind.ru</v-chip>
      <v-chip color="white" value="neurobica">Neurobica</v-chip>
    </v-chip-group>

    <v-card v-if="!showForm" class="mb-6">
      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" @click="openCreateForm">Добавить новость</v-btn>
      </v-card-actions>
    </v-card>

    <v-card v-if="showForm" class="mb-6 pa-4">
      <div class="d-flex justify-space-between align-center mb-4">
        <h2 class="text-h6">
          {{ editingId ? "Редактирование" : "Новая запись" }}
        </h2>
        <v-btn icon @click="closeForm"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-form ref="form" @submit.prevent="save">
        <div class="mb-4">
          <v-textarea
            v-model="sourceText"
            label="Исходный текст"
            placeholder="Вставьте черновик, расшифровку или статью-источник"
            outlined
            auto-grow
            rows="8"
            hint="DeepSeek Pro напишет уникальную статью и заполнит поля. Картинку и alt добавьте вручную."
            persistent-hint
            class="mb-2"
          />
          <v-file-input
            v-model="sourceFile"
            label="Или загрузить исходник (.txt, .md)"
            accept=".txt,.md,.markdown,text/plain,text/markdown"
            prepend-icon="mdi-file-document-outline"
            outlined
            dense
            hide-details
            class="mb-3"
            @change="onSourceFile"
          />
          <v-btn
            color="primary"
            class="mb-4"
            :loading="generatingArticle"
            :disabled="!sourceText || !sourceText.trim()"
            @click="generateFromSource"
          >
            Сгенерировать статью
          </v-btn>
          <v-file-input
            v-model="markdownFile"
            label="Уже готовый Markdown"
            accept=".md,.markdown,.txt,text/markdown,text/plain"
            prepend-icon="mdi-file-upload"
            outlined
            dense
            show-size
            :loading="importingMarkdown"
            hint="Если статья уже в формате CMS — можно загрузить файл вместо генерации."
            persistent-hint
            @change="onMarkdownFile"
          />
          <p v-if="markdownImportHint" class="caption green--text mt-1 mb-0">
            {{ markdownImportHint }}
          </p>
        </div>
        <v-select
          v-model="form.type"
          :items="typeItems"
          label="Тип"
          outlined
          dense
          class="mb-2"
        />
        <v-text-field
          v-model="form.title"
          label="Заголовок"
          outlined
          dense
          required
          class="mb-2"
        />
        <v-text-field
          v-if="form.type !== 'video'"
          v-model.number="form.readingTime"
          label="Время чтения (минуты)"
          placeholder="Только число, например: 8"
          type="number"
          min="0"
          outlined
          dense
          class="mb-2"
        />
        <v-file-input
          v-model="imageFile"
          label="Изображение"
          accept="image/*"
          prepend-icon="mdi-camera"
          outlined
          dense
          @change="onImageChange"
          class="mb-2"
        />
        <v-img
          v-if="form.image && !imageFile && typeof form.image === 'string'"
          :src="getImageUrl(form.image)"
          max-height="120"
          class="mb-2"
        />
        <v-btn
          v-if="form.image"
          small
          text
          color="error"
          @click="deleteImage"
          class="mb-4"
          >Удалить изображение</v-btn
        >

        <div class="mb-4">
          <div class="subtitle-1 mt-2 mb-1">SEO</div>
          <p class="caption grey--text mb-3">
            Редактор заполняет при выкладке. Пустые поля подставятся из
            заголовка и начала текста. Для уже готовой статьи можно заполнить
            только SEO, без переписывания текста.
          </p>
          <v-btn
            color="primary"
            outlined
            class="mb-3"
            :loading="generatingSeo"
            :disabled="!form.title && !(form.content && form.content.length)"
            @click="generateSeo"
          >
            Сгенерировать SEO
          </v-btn>
          <v-text-field
            v-model="form.slug"
            label="Адрес страницы"
            placeholder="migren-osenyu"
            hint="Будет /news/этот-адрес. Латиница и дефисы; если пусто — соберём из заголовка."
            persistent-hint
            outlined
            dense
            class="mb-2"
            @input="onSlugInput"
          />
          <v-text-field
            v-model="form.seoTitle"
            label="SEO-заголовок"
            hint="Тег title и Open Graph. Если пусто — берём заголовок статьи."
            persistent-hint
            outlined
            dense
            class="mb-2"
          />
          <v-textarea
            v-model="form.seoDescription"
            label="SEO-описание"
            :hint="`Сниппет в поиске и соцсетях. ${
              (form.seoDescription || '').length
            } / 160`"
            persistent-hint
            outlined
            dense
            rows="3"
            class="mb-2"
          />
          <v-text-field
            v-model="form.imageAlt"
            label="Alt текст фото"
            hint="Если пусто — заголовок статьи."
            persistent-hint
            outlined
            dense
            class="mb-4"
          />
        </div>

        <template v-if="form.type === 'video'">
          <v-text-field
            v-model="form.videoUrl"
            label="URL видео (embed)"
            placeholder="https://kinescope.io/embed/..."
            outlined
            dense
            class="mb-2"
          />
          <v-file-input
            v-model="posterFile"
            label="Постер видео"
            accept="image/*"
            outlined
            dense
            class="mb-4"
          />
          <v-img
            v-if="form.poster && !posterFile && typeof form.poster === 'string'"
            :src="getImageUrl(form.poster)"
            max-height="120"
            class="mb-4"
          />
        </template>

        <template v-else>
          <v-textarea
            v-model="tableOfContentsText"
            label="Оглавление (одна строка — один пункт)"
            outlined
            dense
            rows="3"
            placeholder="Введение&#10;Методы&#10;Заключение"
            class="mb-4"
          />
          <div class="mb-4">
            <div class="d-flex align-center mb-2">
              <span class="subtitle-2">Блоки контента</span>
              <v-btn
                small
                text
                color="primary"
                @click="addContentBlock"
                class="ml-2"
                >+ Блок</v-btn
              >
            </div>
            <v-card
              v-for="(block, idx) in form.content"
              :key="idx"
              outlined
              class="mb-3 pa-3"
            >
              <div class="d-flex justify-space-between align-center mb-2">
                <v-text-field
                  v-model="block.title"
                  label="Заголовок блока"
                  outlined
                  dense
                  hide-details
                  class="flex-grow-1 mr-2"
                />
                <v-btn icon small @click="removeContentBlock(idx)"
                  ><v-icon small>mdi-delete</v-icon></v-btn
                >
              </div>
              <news-rich-text-editor v-model="block.text" />
            </v-card>
          </div>
        </template>

        <v-switch
          v-model="form.active"
          label="Опубликовано"
          color="primary"
          class="mb-4"
        />
        <div class="d-flex">
          <v-btn type="submit" color="primary" :loading="saving"
            >Сохранить</v-btn
          >
          <v-btn text @click="closeForm" class="ml-2">Отмена</v-btn>
        </div>
      </v-form>
    </v-card>

    <v-card>
      <v-simple-table>
        <template v-slot:default>
          <thead>
            <tr>
              <th>ID</th>
              <th>Тип</th>
              <th>Заголовок</th>
              <th>Адрес</th>
              <th>Создано</th>
              <th>Активна</th>
              <th width="120">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ typeLabel(item.type) }}</td>
              <td>{{ item.title }}</td>
              <td>{{ item.slug || "—" }}</td>
              <td>{{ formatCreatedAt(item.createdAt) }}</td>
              <td>{{ item.active ? "Да" : "Нет" }}</td>
              <td>
                <v-btn small text @click="openEditForm(item)">Изменить</v-btn>
                <v-btn small text color="error" @click="confirmDelete(item)"
                  >Удалить</v-btn
                >
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-card-text v-if="list.length === 0 && !loading"
        >Записей пока нет.</v-card-text
      >
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Удалить запись?</v-card-title>
        <v-card-text
          >«{{ itemToDelete && itemToDelete.title }}» будет
          удалена.</v-card-text
        >
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" text @click="doDelete">Удалить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <error-message-wrapper ref="errorWrapper" />
  </div>
</template>

<script>
import NSApi from "@/api/NSApi";
import { SERVER_URI } from "@/api/NSApi";
import NewsRichTextEditor from "@/components/NewsRichTextEditor.vue";
import ErrorMessageWrapper from "@/components/ErrorMessageWrapper.vue";

const TYPES = [
  { value: "news", text: "Новость" },
  { value: "article", text: "Статья" },
  { value: "video", text: "Видео" },
];

const CYR_SLUG = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "e",
  ж: "zh",
  з: "z",
  и: "i",
  й: "j",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "h",
  ц: "c",
  ч: "ch",
  ш: "sh",
  щ: "sch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
};

function slugifyTitle(title) {
  const s = String(title || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((ch) =>
      Object.prototype.hasOwnProperty.call(CYR_SLUG, ch) ? CYR_SLUG[ch] : ch
    )
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
  return s;
}

export default {
  name: "AdminSiteNewsView",
  components: { NewsRichTextEditor, ErrorMessageWrapper },
  data() {
    return {
      list: [],
      loading: false,
      showForm: false,
      editingId: null,
      saving: false,
      deleteDialog: false,
      itemToDelete: null,
      imageFile: null,
      markdownFile: null,
      sourceFile: null,
      sourceText: "",
      generatingArticle: false,
      generatingSeo: false,
      importingMarkdown: false,
      markdownImportHint: "",
      posterFile: null,
      deleteImageFlag: false,
      selectedSite: "upmind",
      slugManual: false,
      form: {
        type: "news",
        title: "",
        readingTime: null,
        views: 0,
        image: null,
        tableOfContents: [],
        content: [],
        videoUrl: "",
        poster: null,
        active: true,
        slug: "",
        seoTitle: "",
        seoDescription: "",
        imageAlt: "",
      },
    };
  },
  computed: {
    typeItems() {
      return TYPES;
    },
    tableOfContentsText: {
      get() {
        return (this.form.tableOfContents || []).join("\n");
      },
      set(val) {
        this.form.tableOfContents = val
          ? val
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      },
    },
  },
  watch: {
    selectedSite() {
      this.closeForm();
      this.loadList();
    },
    "form.title"(val) {
      if (!this.showForm || this.editingId || this.slugManual) return;
      this.form.slug = slugifyTitle(val);
    },
  },
  mounted() {
    this.loadList();
  },
  methods: {
    formatCreatedAt(isoStr) {
      if (!isoStr) return "—";
      const d = new Date(isoStr);
      if (Number.isNaN(d.getTime())) return "—";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    },
    typeLabel(type) {
      return TYPES.find((t) => t.value === type)?.text || type || "—";
    },
    getImageUrl(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      if (path.startsWith("/api/uploads")) return path;
      return SERVER_URI + path;
    },
    onImageChange(file) {
      if (file) this.form.image = file;
    },
    onSourceFile(file) {
      const picked =
        file && file.name
          ? file
          : Array.isArray(file)
          ? file[0]
          : this.sourceFile;
      if (!picked) return;
      const reader = new FileReader();
      reader.onload = () => {
        this.sourceText = String(reader.result || "");
        this.sourceFile = null;
      };
      reader.readAsText(picked);
    },
    async generateFromSource() {
      const text = (this.sourceText || "").trim();
      if (!text) return;
      this.generatingArticle = true;
      this.markdownImportHint = "";
      try {
        const data = await NSApi.instance.generateNewsFromSource(text);
        this.applyMarkdownImport(data);
        this.markdownImportHint =
          "Статья сгенерирована. Проверьте текст, добавьте картинку и alt, затем сохраните.";
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      } finally {
        this.generatingArticle = false;
      }
    },
    async generateSeo() {
      this.generatingSeo = true;
      this.markdownImportHint = "";
      try {
        const data = await NSApi.instance.generateNewsSeo({
          title: this.form.title,
          content: this.form.content,
        });
        if (data.seoTitle) this.form.seoTitle = data.seoTitle;
        if (data.seoDescription) this.form.seoDescription = data.seoDescription;
        if (data.imageAlt) this.form.imageAlt = data.imageAlt;
        if (!this.form.slug && this.form.title) {
          this.form.slug = slugifyTitle(this.form.title);
        }
        this.markdownImportHint =
          "SEO заполнено. Проверьте поля и сохраните. Текст статьи не менялся.";
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      } finally {
        this.generatingSeo = false;
      }
    },
    applyMarkdownImport(data) {
      if (!data) return;
      const hadSlug = Boolean(data.slug);
      if (hadSlug) this.slugManual = true;
      if (data.type) this.form.type = data.type;
      if (data.title) this.form.title = data.title;
      if (hadSlug) {
        this.form.slug = data.slug;
      } else {
        this.slugManual = false;
        this.form.slug = slugifyTitle(this.form.title);
      }
      if (data.readingTime != null && data.readingTime !== "") {
        this.form.readingTime = Number(data.readingTime);
      }
      if (data.seoTitle != null) this.form.seoTitle = data.seoTitle;
      if (data.seoDescription != null) {
        this.form.seoDescription = data.seoDescription;
      }
      if (Array.isArray(data.tableOfContents)) {
        this.form.tableOfContents = [...data.tableOfContents];
      }
      if (Array.isArray(data.content) && data.content.length) {
        this.form.content = data.content.map((block) => ({
          title: block.title || "",
          text: block.text || "",
        }));
      }
    },
    async onMarkdownFile(file) {
      const picked =
        file && file.name
          ? file
          : Array.isArray(file)
          ? file[0]
          : this.markdownFile;
      if (!picked) return;
      this.importingMarkdown = true;
      this.markdownImportHint = "";
      try {
        const data = await NSApi.instance.parseNewsMarkdown(picked);
        this.applyMarkdownImport(data);
        this.markdownImportHint =
          "Поля заполнены из файла. Картинку и alt добавьте вручную, затем сохраните.";
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      } finally {
        this.importingMarkdown = false;
        this.markdownFile = null;
      }
    },
    onSlugInput() {
      this.slugManual = true;
    },
    deleteImage() {
      this.form.image = null;
      this.imageFile = null;
      this.deleteImageFlag = true;
    },
    addContentBlock() {
      this.form.content.push({ title: "", text: "" });
    },
    removeContentBlock(idx) {
      this.form.content.splice(idx, 1);
    },
    openCreateForm() {
      this.editingId = null;
      this.form = {
        type: "news",
        title: "",
        readingTime: null,
        views: 0,
        image: null,
        tableOfContents: [],
        content: [{ title: "", text: "" }],
        videoUrl: "",
        poster: null,
        active: true,
        slug: "",
        seoTitle: "",
        seoDescription: "",
        imageAlt: "",
      };
      this.imageFile = null;
      this.markdownFile = null;
      this.sourceFile = null;
      this.sourceText = "";
      this.generatingArticle = false;
      this.importingMarkdown = false;
      this.markdownImportHint = "";
      this.posterFile = null;
      this.deleteImageFlag = false;
      this.slugManual = false;
      this.showForm = true;
    },
    openEditForm(item) {
      this.editingId = item.id;
      this.form = {
        type: item.type || "news",
        title: item.title || "",
        readingTime: item.readingTime != null ? Number(item.readingTime) : null,
        views: item.views ?? 0,
        image: item.image || null,
        tableOfContents: Array.isArray(item.tableOfContents)
          ? [...item.tableOfContents]
          : [],
        content:
          Array.isArray(item.content) && item.content.length
            ? item.content.map((b) => ({
                title: b.title || "",
                text: b.text || "",
              }))
            : [{ title: "", text: "" }],
        videoUrl: item.videoUrl || "",
        poster: item.poster || null,
        active: item.active !== false,
        slug: item.slug || "",
        seoTitle: item.seoTitle || "",
        seoDescription: item.seoDescription || "",
        imageAlt: item.imageAlt || "",
      };
      this.imageFile = null;
      this.markdownFile = null;
      this.sourceFile = null;
      this.sourceText = "";
      this.generatingArticle = false;
      this.importingMarkdown = false;
      this.markdownImportHint = "";
      this.posterFile = null;
      this.deleteImageFlag = false;
      this.slugManual = true;
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editingId = null;
    },
    async loadList() {
      this.loading = true;
      try {
        this.list = await NSApi.instance.getNews({
          scope: "site",
          site: this.selectedSite,
        });
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (!this.form.title) return;
      this.saving = true;
      try {
        const payload = {
          type: this.form.type,
          title: this.form.title,
          readingTime:
            this.form.type === "video"
              ? null
              : this.form.readingTime != null && this.form.readingTime !== ""
              ? Number(this.form.readingTime)
              : null,
          tableOfContents:
            this.form.type === "video" ? [] : this.form.tableOfContents,
          content: this.form.type === "video" ? [] : this.form.content,
          videoUrl: this.form.type === "video" ? this.form.videoUrl : null,
          poster:
            this.posterFile ||
            (this.form.poster && typeof this.form.poster === "string"
              ? this.form.poster
              : null),
          active: this.form.active,
          scope: "site",
          site: this.selectedSite,
        };
        payload.slug = this.form.slug;
        payload.seoTitle = this.form.seoTitle;
        payload.seoDescription = this.form.seoDescription;
        payload.imageAlt = this.form.imageAlt;
        if (this.imageFile) payload.image = this.imageFile;
        if (this.editingId && this.deleteImageFlag) payload.deleteImage = true;
        if (this.editingId) {
          await NSApi.instance.updateNews(this.editingId, payload);
        } else {
          await NSApi.instance.createNews(payload);
        }
        await this.loadList();
        this.closeForm();
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async doDelete() {
      if (!this.itemToDelete) return;
      try {
        await NSApi.instance.deleteNews(this.itemToDelete.id);
        await this.loadList();
        this.deleteDialog = false;
        this.itemToDelete = null;
      } catch (e) {
        if (this.$refs.errorWrapper) this.$refs.errorWrapper.showError(e);
      }
    },
  },
};
</script>

<style scoped></style>
